import { useEffect } from 'react';

/** Orientação que o dispositivo trava: totem é retrato, tablet de mesa é paisagem. */
export type OrientacaoKiosk = 'portrait' | 'landscape';

/**
 * Kiosk "soft" de dispositivo fixo (TOTEM ou TABLET): tela cheia, mantém a tela
 * ligada (wake lock), trava a orientação, prende a rolagem do documento e
 * bloqueia gestos que tirariam o cliente do fluxo (zoom de pinça,
 * pull-to-refresh, menu de contexto, seleção).
 *
 * IMPORTANTE: isto NÃO impede 100% sair do app — o lockdown real é do sistema
 * operacional (Android Screen Pinning / Fully Kiosk Browser; iOS Guided Access).
 *
 * Tudo é best-effort e revertido no cleanup; APIs ausentes são ignoradas.
 */
export function useKioskDispositivo(ativo: boolean, orientacao: OrientacaoKiosk): void {
  useEffect(() => {
    if (!ativo || typeof document === 'undefined') return;

    const docEl = document.documentElement;
    const body = document.body;

    // --- Wake Lock: mantém a tela ligada; re-adquire ao voltar a ficar visível ---
    type WakeLockSentinel = { release: () => Promise<void> };
    let wakeLock: WakeLockSentinel | null = null;

    async function adquirirWakeLock(): Promise<void> {
      try {
        const nav = navigator as Navigator & {
          wakeLock?: { request: (t: 'screen') => Promise<WakeLockSentinel> };
        };
        if (nav.wakeLock && document.visibilityState === 'visible') {
          wakeLock = await nav.wakeLock.request('screen');
        }
      } catch {
        /* sem suporte / negado */
      }
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') void adquirirWakeLock();
    }

    void adquirirWakeLock();
    document.addEventListener('visibilitychange', onVisibilityChange);

    // --- Fullscreen: pede na 1ª interação e re-pede se o usuário sair ---
    function pedirFullscreen(): void {
      try {
        if (!document.fullscreenElement && docEl.requestFullscreen) {
          void docEl.requestFullscreen().catch(() => undefined);
        }
      } catch {
        /* ok */
      }
    }

    // --- Orientação travada (best-effort): totem é retrato, tablet paisagem ---
    try {
      const orientation = screen.orientation as ScreenOrientation & {
        lock?: (o: string) => Promise<void>;
      };
      void orientation?.lock?.(orientacao).catch(() => undefined);
    } catch {
      /* desktop / sem suporte */
    }

    // --- Bloqueios de gesto ---
    function onContextMenu(e: Event): void {
      e.preventDefault();
    }
    function onTouchMove(e: TouchEvent): void {
      if (e.touches.length > 1) e.preventDefault(); // pinça
    }
    // Zoom de pinça no Safari: o `user-scalable=no` do meta viewport é IGNORADO
    // pelo iOS desde o iOS 10 (só vale no app da Tela de Início), então quem
    // segura a pinça é o preventDefault destes três — cancelar só o start deixa
    // o gesto escapar em algumas versões.
    function onGesture(e: Event): void {
      e.preventDefault();
    }

    docEl.addEventListener('pointerdown', pedirFullscreen);
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('gesturestart', onGesture as EventListener);
    document.addEventListener('gesturechange', onGesture as EventListener);
    document.addEventListener('gestureend', onGesture as EventListener);

    // --- Estilos: documento travado, sem pull-to-refresh / seleção / realce ---
    type EstiloWebkit = CSSStyleDeclaration & {
      webkitTapHighlightColor?: string;
      webkitTouchCallout?: string;
    };
    const estilo = body.style as EstiloWebkit;
    const estiloDoc = docEl.style;
    const anterior = {
      overscrollBehavior: estilo.overscrollBehavior,
      userSelect: estilo.userSelect,
      touchAction: estilo.touchAction,
      tapHighlight: estilo.webkitTapHighlightColor ?? '',
      touchCallout: estilo.webkitTouchCallout ?? '',
      altura: estilo.height,
      overflow: estilo.overflow,
      docOverscroll: estiloDoc.overscrollBehavior,
      docAltura: estiloDoc.height,
      docOverflow: estiloDoc.overflow,
    };

    // Documento travado: aparelho fixo é APLICATIVO, não página. Sem isto o
    // documento inteiro rola/balança no iPad quando algo passa da dobra por uns
    // poucos pixels, e o cliente vê a tela deslizar debaixo do dedo. As telas do
    // kiosk já se viram sozinhas (a casca é `100dvh` com `overflow: hidden`, e
    // carrinho/checkout/produto são diálogos que rolam por dentro), então
    // ninguém depende da rolagem do documento aqui.
    estiloDoc.height = '100%';
    estiloDoc.overflow = 'hidden';
    estiloDoc.overscrollBehavior = 'none';
    estilo.height = '100%';
    estilo.overflow = 'hidden';
    estilo.overscrollBehavior = 'none';
    estilo.userSelect = 'none';
    estilo.touchAction = 'pan-x pan-y';
    estilo.webkitTapHighlightColor = 'transparent';
    // Lupa do toque longo do iOS. O catálogo já bloqueava por conta própria;
    // carrinho, checkout e confirmação ficavam de fora — e é justo onde o toque
    // longo do cliente abria a lupa por cima do botão.
    estilo.webkitTouchCallout = 'none';

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      docEl.removeEventListener('pointerdown', pedirFullscreen);
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('gesturestart', onGesture as EventListener);
    document.removeEventListener('gesturechange', onGesture as EventListener);
    document.removeEventListener('gestureend', onGesture as EventListener);
      void wakeLock?.release().catch(() => undefined);
      estilo.overscrollBehavior = anterior.overscrollBehavior;
      estilo.height = anterior.altura;
      estilo.overflow = anterior.overflow;
      estiloDoc.overscrollBehavior = anterior.docOverscroll;
      estiloDoc.height = anterior.docAltura;
      estiloDoc.overflow = anterior.docOverflow;
      estilo.userSelect = anterior.userSelect;
      estilo.touchAction = anterior.touchAction;
      estilo.webkitTapHighlightColor = anterior.tapHighlight;
      estilo.webkitTouchCallout = anterior.touchCallout;
    };
  }, [ativo, orientacao]);
}
